import type { DialogProps } from '@mui/material';
import { DialogContentText, TextField } from '@mui/material';
import { useState } from 'react';

import { Or2Button } from 'Components/button/Or2Button';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';

import { useCreateFolder } from '@/modules/files/hooks/useCreateFolder';

import type { RootOwner } from '../../file-picker/FilePicker';

interface Props extends DialogProps {
  owner: string;
  rootOwnerId: string;
  parentId: string | null | undefined;
  parentName: string;
  rootOwner: RootOwner;
  onClose: () => void;
}

export const CreateFolderDialog = ({
  owner,
  rootOwnerId,
  parentId,
  parentName,
  rootOwner,
  onClose,
  ...other
}: Props) => {
  const [filename, setFilename] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(false);

  const [createFolder] = useCreateFolder(rootOwnerId);

  const handleFilenameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilename(event.target.value);
  };

  const onCreate = () => {
    createFolder({
      variables: {
        data: {
          filename,
          owner,
          parent: parentId || null,
          type: rootOwner.type,
        },
      },
    });
  };

  const handleCreate = async () => {
    if (!filename) {
      onClose();
      return;
    }

    setCreating(true);
    setError(false);

    try {
      onCreate();
      setCreating(false);
      setFilename('');
      onClose();
    } catch {
      setCreating(false);
      setError(true);
    }
  };

  const handleInputKeyDown = (evt: React.KeyboardEvent) => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
      handleCreate();
    }
  };

  return (
    <Or2Dialog
      maxWidth="xs"
      title="Create a new folder"
      actions={
        <>
          <Or2Button color="secondary" onClick={onClose}>
            Cancel
          </Or2Button>
          <Or2Button onClick={handleCreate} color="primary" disabled={creating}>
            Create
          </Or2Button>
        </>
      }
      loading={creating}
      onClose={onClose}
      aria-labelledby="new-folder-dialog-title"
      disableRestoreFocus
      {...other}
    >
      <DialogContentText>
        Create a new folder in <strong>{parentName}</strong>
      </DialogContentText>
      <TextField
        autoFocus
        margin="dense"
        id="newfolder"
        label="Folder Name"
        type="text"
        value={filename}
        error={error}
        helperText={error ? 'Error creating folder' : ''}
        onChange={handleFilenameChange}
        fullWidth
        slotProps={{
          htmlInput: {
            onKeyDown: handleInputKeyDown,
          },
        }}
      />
    </Or2Dialog>
  );
};
